import React, { useEffect, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import "../../assets/style/BranchForm.css";
import { getData, postData } from "../Commonfunction/CommonFunction";
import { toast } from "react-toastify";
import { convertToRaw, EditorState, ContentState } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

// CSS styles for input borders
const inputStyles = {
  borderInput: {
    display: "block",
    width: "100%",
    padding: "0.5rem",
    fontSize: "1rem",
    lineHeight: "1.5",
    color: "#495057",
    backgroundColor: "#fff",
    backgroundClip: "padding-box",
    border: "1px solid #ced4da",
    borderRadius: "0.25rem",
    transition: "border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out",
  },
  borderInputError: {
    border: "1px solid #dc3545",
  },
  editorBorder: {
    border: "1px solid #ced4da",
    borderRadius: "0.25rem",
    padding: "0.5rem",
    minHeight: "200px",
  },
};

// Validation schema using Yup
const validationSchema = Yup.object().shape({
  email_title: Yup.string().required("Title is required"),
  email_content: Yup.string().required("Email content is required"),
});

const EmailTemplatesForms = ({ onClose, id, fetchData }) => {
  const [templateData, setTemplateData] = useState(null);
  const [editorState, setEditorState] = useState(EditorState.createEmpty());
  const [isLoading, setIsLoading] = useState(false);

  // Fetch template data if editing
  useEffect(() => {
    if (id) {
      fetchTemplateData();
    }
  }, [id]);

  const fetchTemplateData = async () => {
    setIsLoading(true);
    try {
      const result = await getData(`/api/get-email-template/${id}`);
      if (result.success) {
        setTemplateData(result.data);

        // Initialize editor with existing content
        if (result.data?.email_content) {
          const blocksFromHtml = htmlToDraft(result.data.email_content);
          const contentState = ContentState.createFromBlockArray(
            blocksFromHtml.contentBlocks,
            blocksFromHtml.entityMap
          );
          setEditorState(EditorState.createWithContent(contentState));
        }
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error("Error fetching template data:", error);
      toast.error("Failed to load template data");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const requestUrl = id
        ? `/api/update-email-template/${id}`
        : "/api/create-email-template";

      const response = await postData(requestUrl, values);

      if (response.success) {
        toast.success(response.message);
        fetchData();
        onClose();
      } else {
        toast.error(response.message || "Operation failed");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "An error occurred");
    } finally {
      setSubmitting(false);
    }
  };

  // Editor configuration
  const editorToolbar = {
    options: ["inline", "list", "link", "blockType", "remove"],
    inline: {
      inDropdown: false,
      options: ["bold", "italic", "underline"],
    },
    list: {
      inDropdown: false,
      options: ["unordered", "ordered"],
    },
    link: {
      inDropdown: false,
      options: ["link"],
    },
    blockType: {
      inDropdown: true,
      options: ["Normal"],
    },
    remove: { className: undefined },
  };

  const initialValues = {
    email_title: templateData?.email_title || "",
    email_content: templateData?.email_content || "",
  };

  if (isLoading) {
    return <div className="loading-indicator">Loading template data...</div>;
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikBag) => {
        // Convert editor content to HTML
        const contentHtml = draftToHtml(
          convertToRaw(editorState.getCurrentContent())
        );
        handleSubmit({ ...values, email_content: contentHtml }, formikBag);
      }}
    >
      {({ errors, touched, setFieldValue, isSubmitting }) => (
        <Form>
          <div className="form-body">
            <div className="grid-row">
              <div className="grid-col-12">
                <div className="form-group">
                  <label
                    htmlFor="email_title"
                    style={{ display: "block", marginBottom: "0.5rem" }}
                  >
                    Title
                  </label>
                  <Field
                    id="email_title"
                    name="email_title"
                    type="text"
                    placeholder="Enter email title"
                    style={{
                      ...inputStyles.borderInput,
                      ...(errors.email_title && touched.email_title
                        ? inputStyles.borderInputError
                        : {}),
                    }}
                  />
                  <ErrorMessage
                    name="email_title"
                    component="div"
                    className="error-message"
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="grid-row" style={{ marginTop: "1rem" }}>
              <div className="grid-col-12">
                <div className="form-group">
                  <label
                    htmlFor="email_content"
                    style={{ display: "block", marginBottom: "0.5rem" }}
                  >
                    Email Content
                  </label>
                  <div style={inputStyles.editorBorder}>
                    <Editor
                      editorState={editorState}
                      onEditorStateChange={(newState) => {
                        setEditorState(newState);
                        // Update Formik value
                        const contentHtml = draftToHtml(
                          convertToRaw(newState.getCurrentContent())
                        );
                        setFieldValue("email_content", contentHtml);
                      }}
                      wrapperClassName="rich-editor-wrapper"
                      editorClassName="rich-editor-content"
                      toolbarClassName="rich-editor-toolbar"
                      toolbar={editorToolbar}
                    />
                  </div>
                  <ErrorMessage
                    name="email_content"
                    component="div"
                    className="error-message"
                    style={{
                      color: "#dc3545",
                      fontSize: "0.875rem",
                      marginTop: "0.25rem",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-footer">
            <button type="button" className="btn btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-submit">
              Submit
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default EmailTemplatesForms;
